import { useEffect, useState } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { Plus, ChevronDown, ChevronUp } from 'lucide-react';

const PRIORITIES = ['low','normal','high','urgent'];
const STATUS_COLOR = {
  open:'bg-yellow-100 text-yellow-700', assigned:'bg-blue-100 text-blue-700',
  en_route:'bg-purple-100 text-purple-700', on_site:'bg-indigo-100 text-indigo-700',
  completed:'bg-green-100 text-green-700', cancelled:'bg-gray-100 text-gray-500',
};

function JobForm({ clients, workers, onSave, onCancel, initial }) {
  const [f, setF] = useState(initial || { title:'', description:'', address:'', priority:'normal', client_id:'', client_name:'', assigned_worker_id:'', assigned_worker_name:'', scheduled_at:'' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    try { await onSave(f); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <h3 className="font-bold text-gray-900">{initial ? 'Edit Job' : 'New Job'}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Title *</label>
          <input required value={f.title} onChange={e => set('title', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
          <textarea rows={2} value={f.description} onChange={e => set('description', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Address</label>
          <input value={f.address} onChange={e => set('address', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Scheduled</label>
          <input type="datetime-local" value={f.scheduled_at} onChange={e => set('scheduled_at', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Priority</label>
          <select value={f.priority} onChange={e => set('priority', e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
            {PRIORITIES.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Client</label>
          <select value={f.client_id} onChange={e => {
            const c = clients.find(x => x.client_id === e.target.value);
            set('client_id', e.target.value);
            set('client_name', c?.name || '');
          }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
            <option value="">No client</option>
            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Assign Worker</label>
          <select value={f.assigned_worker_id} onChange={e => {
            const w = workers.find(x => x.user_id === e.target.value);
            set('assigned_worker_id', e.target.value);
            set('assigned_worker_name', w?.name || '');
          }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
            <option value="">Unassigned</option>
            {workers.map(w => <option key={w.user_id} value={w.user_id}>{w.name}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
          {initial ? 'Save Changes' : 'Create Job'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-200 text-sm rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function JobsPage() {
  const [jobs, setJobs]       = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    load();
    api.get('/opsive/clients').then(setClients).catch(() => {});
    api.get('/opsive/workers').then(setWorkers).catch(() => {});
  }, []); // eslint-disable-line

  const load = () => api.get('/opsive/jobs').then(setJobs).catch(e => toast.error(e.message));

  const create = async f => {
    await api.post('/opsive/jobs', f);
    toast.success('Job created');
    setCreating(false);
    load();
  };

  const save = async f => {
    await api.patch(`/opsive/jobs/${editing.job_id}`, f);
    toast.success('Job updated');
    setEditing(null);
    load();
  };

  const remove = async id => {
    if (!window.confirm('Delete this job?')) return;
    await api.delete(`/opsive/jobs/${id}`);
    toast.success('Deleted');
    load();
  };

  const addNote = async job_id => {
    if (!noteText.trim()) return;
    await api.patch(`/opsive/jobs/${job_id}`, { note: noteText });
    setNoteText('');
    load();
    toast.success('Note added');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Jobs</h1>
        <button onClick={() => setCreating(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> New Job
        </button>
      </div>

      {creating && <JobForm clients={clients} workers={workers} onSave={create} onCancel={() => setCreating(false)} />}

      <div className="space-y-2">
        {jobs.map(job => (
          <div key={job.job_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {editing?.job_id === job.job_id ? (
              <div className="p-4">
                <JobForm clients={clients} workers={workers} initial={job} onSave={save} onCancel={() => setEditing(null)} />
              </div>
            ) : (
              <>
                <div className="px-5 py-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{job.title}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[job.status]}`}>
                        {job.status.replace('_',' ')}
                      </span>
                      <span className="text-xs text-gray-400 capitalize">{job.priority}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {job.client_name || '—'} · {job.assigned_worker_name || 'Unassigned'}
                      {job.address ? ` · ${job.address}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setEditing(job)} className="text-xs text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => remove(job.job_id)} className="text-xs text-red-500 hover:underline">Delete</button>
                    <button onClick={() => setExpanded(expanded === job.job_id ? null : job.job_id)} className="text-gray-400">
                      {expanded === job.job_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {expanded === job.job_id && (
                  <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 space-y-4">
                    {job.description && <p className="text-sm text-gray-600">{job.description}</p>}

                    {/* Status log */}
                    {job.status_log?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Status History</p>
                        <div className="space-y-1">
                          {job.status_log.map((s, i) => (
                            <div key={i} className="text-xs text-gray-500 flex gap-2">
                              <span className="font-medium capitalize">{s.status.replace('_',' ')}</span>
                              <span>by {s.by}</span>
                              <span className="text-gray-300">{new Date(s.ts).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Notes</p>
                      <div className="space-y-1 mb-2">
                        {(job.notes || []).map(n => (
                          <div key={n.note_id} className="bg-white rounded-lg px-3 py-2 text-sm text-gray-700 border border-gray-100">
                            {n.text} <span className="text-xs text-gray-400 ml-2">— {n.author}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input value={noteText} onChange={e => setNoteText(e.target.value)}
                          placeholder="Add a note…"
                          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                        <button onClick={() => addNote(job.job_id)}
                          className="text-sm px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700">
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {jobs.length === 0 && !creating && (
          <div className="text-center py-16 text-gray-400 text-sm">No jobs yet. Create one above.</div>
        )}
      </div>
    </div>
  );
}
